/* eslint-disable @typescript-eslint/consistent-type-imports */
/* eslint-disable @typescript-eslint/prefer-optional-chain */

/* eslint-disable no-undef */
import ProfileView from "./ProfileView";
import { UserDetails } from "types";
import env from "configs";
import { UPLOAD_IMAGE } from "../../../../services/CONSTANTS";
import { useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "hooks";
import { getLongDate } from "utils";
import { loginSuccess } from "services/auth.service";
import { fetchUser } from "../../../../redux/slices/auth.slice";
//  import { getUser } from "../../../../services/users.service";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

// How often to check for a newly posted letter while this page is open.
// 20s keeps it feeling close to live without hammering the backend -
// adjust to taste.
const LETTER_POLL_INTERVAL_MS = 20000;

// Appends a `v=<timestamp>` query param to each letter URL so the
// <iframe> in ProfileView is always forced to reload after a refresh,
// even if the backend happens to return the exact same URL string as
// before (e.g. a Cloudinary upload that overwrites the same public_id
// rather than generating a new one).
function bustLetterCache(letters: Record<string, any>, version: number): Record<string, any> {
  const withVersion = (url?: string) =>
    url ? `${url}${url.includes("?") ? "&" : "?"}v=${version}` : url;

  return {
    ...letters,
    postingLetter: withVersion(letters?.postingLetter),
    zonalCoordinatingPrincipalLetter: withVersion(letters?.zonalCoordinatingPrincipalLetter)
  };
}

export const ProfileContainer = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const updatedUser = location?.state?.user?.data?.DATA;
  // console.log(updatedUser)

  const [postingLetter, setPosttingLetter] = useState<null | string | any>("");
  const [imageUrl, setImageUrl] = useState<null | string | any>("");

  useEffect(() => {
    loginSuccess()
      .then(() => {
        console.log("user login successful auth service");
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);
  const { user } = useAppSelector((state) => state.auth);
  // console.log(user)
  const [userSaved, setUserSaved] = useState<any>(user);
  // Bumped on every successful refresh - used to force the letter
  // iframe to actually reload even if the underlying letter URL
  // happens to be the exact same string as before (e.g. a Cloudinary
  // upload that overwrites the same public_id). Without this, React
  // sees an unchanged `src` prop and skips reloading the iframe
  // entirely, so the preview can look stale even though the fetch
  // itself succeeded.
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number>(Date.now());

  const refreshUser = () => {
    dispatch(fetchUser(userSaved.user?._id))
      .unwrap()
      .then((res) => {
        setUserSaved(res);
        setLastRefreshedAt(Date.now());
        setPosttingLetter(res);
      })
      .catch((err) => console.log(err));
  };

  // Initial fetch on mount
  useEffect(() => {
    refreshUser();
  }, [dispatch]);

  // Poll for updates while this page stays open, so a letter posted
  // by an admin in a completely different browser/session still
  // shows up here without the viewer needing to refresh manually.
  // Pausing while the tab is hidden avoids wasted requests when the
  // page is backgrounded.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        refreshUser();
      }
    }, LETTER_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dispatch]);

  const dateOfBirth = getLongDate(userSaved?.user?.dateOfBirth);
  const dateOfFirstAppointment = getLongDate(userSaved?.user?.dateOfFirstAppointment);
  const dateOfRetirement = getLongDate(userSaved?.user?.dateOfRetirement);

  const userDetails: UserDetails = {
    subjectsTaught: updatedUser?.subjectsTaught ?? userSaved?.user?.subjectsTaught,
    _id: userSaved?.user?._id,
    staffName: updatedUser?.staffName?.firstName ?? userSaved?.user?.staffName?.firstName,
    dateOfBirth,
    dateOfFirstAppointment,
    dateOfRetirement,
    ogNumber: updatedUser?.ogNumber ?? userSaved?.user?.ogNumber,
    phoneNumber: updatedUser?.phoneNumber ?? userSaved?.user?.phoneNumber,
    // Deliberately NOT falling back to `updatedUser?.letters` here: that value
    // is a one-time snapshot frozen at navigation time (from location.state)
    // and never updates again for the life of this page. Using it as a
    // fallback would let a stale letter permanently shadow every fresh
    // result that refreshUser()/polling brings back afterwards.
    letters: bustLetterCache(
      postingLetter?.letters ?? userSaved?.user?.letters ?? {},
      lastRefreshedAt
    ),
    tscFileNumber: updatedUser?.tscFileNumber ?? userSaved?.user?.tscFileNumber,
    schoolOfPresentPosting:
      updatedUser?.schoolOfPresentPosting?.nameOfSchool ??
      userSaved?.user?.schoolOfPresentPosting?.nameOfSchool,
    schoolOfPreviousPosting:
      updatedUser?.schoolOfPreviousPosting?.nameOfSchool ??
      userSaved?.user?.schoolOfPreviousPosting?.nameOfSchool,
    residentialAddress: updatedUser?.residentialAddress ?? userSaved?.user?.residentialAddress,
    zone: updatedUser?.zone ?? userSaved?.user?.zone,
    division: updatedUser?.division ?? userSaved?.user?.division,
    nationality: updatedUser?.nationality ?? userSaved?.user?.nationality,
    stateOfOrigin: updatedUser?.stateOfOrigin ?? userSaved?.user?.stateOfOrigin,
    lgOfOrigin: updatedUser?.lgOfOrigin ?? userSaved?.user?.lgOfOrigin,
    ward: updatedUser?.ward ?? userSaved?.user?.ward,
    staffType: updatedUser?.staffType ?? userSaved?.user?.staffType,
    qualifications: updatedUser?.qualifications ?? userSaved?.user?.qualifications,
    dateOfPresentSchoolPosting:
      updatedUser?.dateOfPresentSchoolPosting ?? userSaved?.user?.dateOfPresentPosting,
    cadre: updatedUser?.cadre ?? userSaved?.user?.cadre,
    // dateOfFirstAppointment?: Date;
    // dateOfLastPromotion?: Date;
    // dateOfBirth?: Date;
    nextOfKinAddress: updatedUser?.nextOfKinAddress ?? userSaved?.user?.nextOfKinAddress,
    nameOfNextOfKin: updatedUser?.nameOfNextOfKin ?? userSaved?.user?.nameOfNextOfKin,
    nextOfKinPhoneNumber:
      updatedUser?.nextOfKinPhoneNumber ?? userSaved?.user?.nextOfKinPhoneNumber,
    gradeLevel: updatedUser?.gradeLevel ?? userSaved?.user?.gradeLevel,
    pfa: updatedUser?.pfa ?? userSaved?.user?.pfa,
    pensionNumber: updatedUser?.pensionNumber ?? userSaved?.user?.pensionNumber,
    position: updatedUser?.position ?? userSaved?.user?.position,
    // dateOfRetirement?: Date;
    professionalStatus: updatedUser?.professionalStatus ?? userSaved?.user?.professionalStatus,
    email: updatedUser?.email ?? userSaved?.user?.email
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];

      const formData = new FormData();
      formData.append("image", selectedFile);

      try {
        const response = await axios.post(`${env.API_BASE_URL}/${UPLOAD_IMAGE}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        });

        if (response.data) {
          toast.success("Upload successful");
          alert("Picture uploaded successfully!");
          setImageUrl(response.data);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        alert("Failed to upload image.");
      }
    }
  };

  return (
    <>
      <ProfileView
        loading={false}
        userDetails={userDetails}
        image={imageUrl}
        pictureUpload={handleFileChange}
      ></ProfileView>
    </>
  );
};
